/**
 * Règles de classement du test caméra / micro d'entrée au live.
 *
 * Ce fichier est volontairement PUR : aucun import, aucune dépendance React ou
 * React Native. Deux raisons :
 *  - il est testable sans monter de composant ;
 *  - la page de vérification cross-navigateur (`__devtools__/check-harness.html`)
 *    l'importe directement en module ES, donc on vérifie les vraies règles et
 *    non une copie qui divergerait.
 *
 * Principe de détection : on n'interroge PAS l'état des permissions via
 * `navigator.permissions.query()` — cette API n'existe ni sur Firefox ni sur
 * Safari, et son échec y est aujourd'hui interprété comme un refus. On tente
 * d'ouvrir le flux et on classe le résultat, ce qui répond partout.
 */

export const STATUS = {
    OK: "ok", // l'appareil répond et donne du signal
    WARN: "warn", // il répond, mais on ne peut pas conclure
    BAD: "bad", // erreur explicite : refus, absence, conflit
    PENDING: "pending", // rien n'a encore été tenté
};

/**
 * Où en est l'analyse de l'image, sur le web.
 *
 * Trois états, pas deux booléens : « a fini » et « a tranché » se déduisaient
 * l'un de l'autre — trancher implique avoir fini — si bien que la paire rendait
 * exprimable une quatrième situation qui n'existe pas. Le vocabulaire est ici,
 * à côté des autres verdicts, parce que trois fichiers le lisent : les deux
 * règles pures et le hook qui mesure.
 */
export const IMAGE = {
    /** L'analyse regarde encore, aucun verdict. */
    LOOKING: "looking",
    /** Elle a lu l'image et tranché — noire ou nette, `isObstructed` le dit. */
    JUDGED: "judged",
    /** Elle a renoncé, faute d'image lisible. C'est aussi le cas du natif. */
    GAVE_UP: "gave_up",
}

export const CAUSE = {
    NOT_ASKED: "not_asked",
    GRANTED: "granted",
    DENIED: "denied",
    BLOCKED_BY_SYSTEM: "blocked_by_system",
    NO_DEVICE: "no_device",
    IN_USE: "in_use",
    DARK_IMAGE: "dark_image",
    UNKNOWN: "unknown",
};

/**
 * Traduit une erreur getUserMedia en cause métier.
 *
 * Cas particulier Windows et macOS : quand la confidentialité du système bloque
 * l'accès, le navigateur lève un NotAllowedError dont le message mentionne le
 * système. C'est un cas distinct du refus utilisateur, parce que le geste de
 * réparation n'est pas le même — réglages de l'OS et non du navigateur.
 */
export function classifyMediaError(error) {
    if (!error) return CAUSE.GRANTED;

    const name = error.name || "";
    const message = error.message || "";

    if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
        return /system|système|policy/i.test(message) ? CAUSE.BLOCKED_BY_SYSTEM : CAUSE.DENIED;
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
        return CAUSE.NO_DEVICE;
    }
    if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") {
        return CAUSE.IN_USE;
    }
    return CAUSE.UNKNOWN;
}

/**
 * Verdict caméra. L'image sombre est le seul cas ⚠️ : le flux fonctionne, mais
 * on ne peut pas distinguer un cache d'objectif d'une pièce mal éclairée.
 */
export function cameraStatus(cause) {
    switch (cause) {
        case CAUSE.NOT_ASKED:
            return STATUS.PENDING;
        case CAUSE.GRANTED:
            return STATUS.OK;
        case CAUSE.DARK_IMAGE:
            return STATUS.WARN;
        default:
            return STATUS.BAD;
    }
}

/**
 * Verdict micro. Le flux ouvert sans signal reste ⚠️ et non ❌ : on ne sait pas
 * distinguer un micro coupé d'un élève qui ne parle pas. Le ⚠️ prévient bien
 * l'équipe — tout voyant mesuré non vert le fait depuis le 07/09 ; la règle est
 * dans `shouldAlertTechnician`, plus bas.
 */
export function microphoneStatus(cause, hasSignal) {
    switch (cause) {
        case CAUSE.NOT_ASKED:
            return STATUS.PENDING;
        case CAUSE.GRANTED:
            return hasSignal ? STATUS.OK : STATUS.WARN;
        default:
            return STATUS.BAD;
    }
}

/**
 * Verdict caméra, une fois tout ce qu'on sait rassemblé.
 *
 * L'ordre des tests porte la règle du lot, et il n'est pas interchangeable :
 * **une image noire ne peut jamais être validée par l'élève.** L'écran lui
 * annonce que son objectif est masqué ; lui proposer dans la foulée de
 * confirmer qu'il se voit bien, et accepter ce oui, revenait à entrer en séance
 * avec une caméra couverte après le lui avoir dit.
 *
 * Ce que l'analyse sait, elle le tranche — dans les deux sens. Une image lue
 * et non noire vaut « on te voit », sans rien demander : c'est la règle du
 * micro, validé au premier son sans qu'on demande à l'élève s'il s'est bien
 * entendu. L'écran lui demandait pourtant « est-ce que tu te vois bien ? »
 * devant une image que l'analyse venait de juger bonne — une question de trop,
 * et la dernière du parcours.
 *
 * La question ne subsiste que là où rien n'a lu l'image : sur téléphone, où
 * l'analyse n'existe pas, et sur le web quand elle a renoncé faute d'image
 * lisible — les deux valent `IMAGE.GAVE_UP`.
 *
 * Une réponse de l'élève passe avant l'analyse, et non l'inverse : elle est
 * testée d'abord. Le renoncement de l'analyse n'arrête pas les relevés — sur
 * Windows, une première ouverture de caméra dépasse couramment le délai, et
 * l'image finit par arriver. Sans cet ordre, l'écran demandait « te vois-tu
 * bien ? », l'élève répondait non, puis l'image arrivait, l'analyse la jugeait
 * nette et le voyant repassait au vert : sa réponse annulée par une mesure qui
 * ne sait rien du cadrage.
 *
 * @param {string} image  où en est l'analyse, une valeur de `IMAGE`
 */
export function cameraVerdict({
    isVideoMuted,
    opened,
    isObstructed,
    image = IMAGE.GAVE_UP,
    seesSelf,
    isWeb,
}) {
    if (isVideoMuted) return STATUS.WARN
    if (opened !== STATUS.OK) return opened
    if (isObstructed) return STATUS.WARN
    // Sur le web, on ne conclut rien avant que l'analyse ait parlé.
    if (isWeb && image === IMAGE.LOOKING) return STATUS.PENDING
    if (seesSelf === false) return STATUS.WARN
    // Un juge a dit oui — l'analyse ou l'élève. Sinon on attend encore.
    if (image === IMAGE.JUDGED || seesSelf === true) return STATUS.OK
    return STATUS.PENDING
}

/**
 * L'équipe est prévenue dès qu'un voyant mesuré n'est pas au vert — quel qu'il soit.
 *
 * C'est l'arbitrage du 26/08, rétabli le 07/09 après une parenthèse où seul un
 * micro en erreur explicite escaladait. Le raisonnement d'alors — sans voix on
 * ne participe pas, sans image on participe encore — décrivait la gêne de
 * l'élève, pas ce que l'équipe a besoin de savoir. Un élève qui entre sans
 * caméra, sans son, ou sur un réseau qui hache est un élève dont la séance se
 * passera mal ; le canal est là pour le voir, pas pour trier.
 *
 * Ce que ça ne fait plus : promettre quelque chose à l'élève. Le signalement
 * est une information d'équipe, pas un engagement — voir `PreCallScreen`.
 *
 * `PENDING` ne compte pas : **on n'alerte pas sur ce qu'on n'a pas mesuré.**
 * Le compter comme un défaut faisait partir un `@here` « l'élève est entré sans
 * que son matériel fonctionne » à chaque « Entrer sans finir le test » — donc à
 * chaque séance, pour tout élève pressé, et à propos d'un matériel dont on ne
 * sait précisément rien. Un canal qui crie sur du vide est un canal qu'on
 * apprend à ignorer, et c'est le volume de `#ops-notifs` qui en dépend.
 *
 * Une étape absente du bilan est ignorée de la même façon — c'est ce qui permet
 * d'appeler cette règle avec un sous-ensemble de verdicts.
 */
export function shouldAlertTechnician(verdicts = {}) {
    return failingDevices(verdicts).length > 0;
}

/**
 * Le bilan matériel tel qu'il doit être lu pour décider d'oublier le souvenir
 * — c'est CE bilan, et non les verdicts bruts, que `PreCallScreen.
 * alertTechnician` passe à `shouldAlertTechnician` pour cette décision
 * précise (il reste passé tel quel pour décider si on alerte l'équipe, et
 * pour le message/la catégorie : seule la décision « oublier » filtre ici).
 *
 * Une caméra au ⚠️ uniquement parce que l'élève a coupé sa vidéo ne doit pas
 * en faire partie : `cameraVerdict` rend ⚠️ dès `isVideoMuted`, avant même de
 * regarder l'état réel du flux — ça ne dit donc rien de la configuration du
 * poste (P-3925, souvenir effacé à tort le 29/09). Même raisonnement que le
 * réseau, déjà exclu pour la même raison en ne le passant pas du tout à
 * `shouldAlertTechnician` — ici la caméra DOIT rester dans le bilan quand un
 * vrai défaut la fait échouer, donc l'exclusion ne peut pas être une simple
 * omission au call site : elle dépend d'une condition (`isVideoMuted`), d'où
 * cette fonction dédiée, testable sans monter `PreCallScreen` (P-3409).
 *
 * @param {{microphone?: string, speaker?: string, camera?: string}} verdicts
 * @param {{isVideoMuted?: boolean}} context
 */
export function materialBilanForMemory({ microphone, speaker, camera } = {}, { isVideoMuted } = {}) {
    return {
        microphone,
        speaker,
        camera: isVideoMuted ? undefined : camera,
    }
}

/**
 * L'ordre de gravité des appareils — et le seul endroit qui en décide.
 *
 * Il sert à deux choses à la fois : choisir l'étiquette du signalement, et
 * ordonner ce que le message énumère. Le micro d'abord, parce que sans voix on
 * ne participe pas à une séance de tutorat ; puis les haut-parleurs, la caméra,
 * et le réseau en dernier — le seul dont l'élève n'est pas responsable.
 */
const DEVICE_SEVERITY = ["microphone", "speaker", "camera", "network"];

/**
 * Les voyants qui ne sont pas au vert, du plus grave au moins grave.
 *
 * Même lecture que `shouldAlertTechnician` : ce qui n'a pas été mesuré ne
 * compte pas, et une étape absente du bilan est ignorée.
 */
export function failingDevices(verdicts = {}) {
    return DEVICE_SEVERITY.filter(
        (step) =>
            verdicts[step] !== undefined &&
            verdicts[step] !== STATUS.PENDING &&
            verdicts[step] !== STATUS.OK
    );
}

/**
 * Sous quelle étiquette le signalement part — `null` s'il n'y a rien à signaler.
 *
 * Le message partait sous « Audio » en dur : une caméra refusée ou un réseau
 * qui hache arrivait dans le canal annoncée comme un problème de son, et le
 * technicien commençait par la mauvaise piste.
 *
 * Les valeurs reflètent `ISSUE_REPORT_CATEGORY` — ce fichier reste sans import,
 * c'est le prix de sa pureté ; toute nouvelle catégorie se recopie ici.
 */
export function alertCategory(verdicts = {}) {
    const worst = failingDevices(verdicts)[0];
    if (!worst) return null;
    if (worst === "camera") return "video";
    if (worst === "network") return "connection";
    return "audio";
}
